import { useEffect, useRef, useContext, useState } from "react";
import InfiniteScroll from "react-infinite-scroll-component";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { RiAlarmWarningLine } from "react-icons/ri";
import { Avatar } from "@chakra-ui/react";

// Components
import SkeletonLoader from "../Skeleton/PostLoader";
import UserPostShare from "../SingleUserPost/UserPostShare/UserPostShare";
import VideoItem from "./VideoItem";

// Context
import { MapContext } from "../../context/MapContext";

// Assets
import userLock from "../../assets/images/lock-circle.svg";

// Utils
import { getDistanceFromLatLonInKm } from "../../utils/utils";

// Styles
import "react-responsive-carousel/lib/styles/carousel.css";

/**
 * DashboardSide component - Displays a feed of events/posts with infinite scrolling
 */
function DashboardSide({
  events, // Array of events/posts to display
  callEvents, // Function to fetch more events
  handleSinglePost, // Function to handle clicking on a post
  token, // User authentication token
  user, // Current user data
  openReportModal, // Function to open report modal
  updateCounts, // Function to update interaction counts
  isOpen, // Modal open state
  onClose, // Function to close modal
  coords, // User's current coordinates
}) {
  const darktheme = useSelector((state) => state.features.settings.darktheme);
  const loaderRef = useRef(null);
  const router = useLocation();
  const { mapRef } = useContext(MapContext);
  const [hoverTimeout, setHoverTimeout] = useState(null);

  // Set up intersection observer for infinite scrolling
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      const target = entries[0];
      if (target.isIntersecting) {
        callEvents(true);
      }
    });

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        observer.unobserve(loaderRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Handles post hover to focus map on post location
   * @param {Object} post - The post being hovered
   */
  const handlePostHover = (post) => {
    if (mapRef.current && post?.coords) {
      mapRef.current.flyTo({
        center: [post.coords.longitude, post.coords.latitude],
        zoom: 15,
        speed: 0.8,
        essential: true,
      });
    }
  };

  /**
   * Combines videos and images into a single media array
   * @param {Object} post - The post containing media
   * @returns {Array} - Combined media array with type indicators
   */
  const getCombinedMedia = (post) => {
    return [
      ...(post?.media?.videos?.map((url) => ({
        type: "video",
        url,
      })) || []),
      ...(post?.media?.images?.map((url) => ({
        type: "image",
        url,
      })) || []),
    ];
  };

  /**
   * Renders a media grid based on number of media items
   * @param {Array} allMedia - Array of media objects
   * @param {Object} post - The post containing the media
   * @returns {JSX.Element} - Media grid element
   */
  const renderMediaGrid = (allMedia, post) => {
    const isExplore = router?.pathname === "/dashboard/explore";

    if (allMedia.length === 0) {
      return (
        <div className="px-2 rounded-[10px] cursor-pointer">
          <div
            onClick={() => handleSinglePost(post)}
            className={`${
              isExplore ? "h-[300px]" : "h-[200px]"
            } w-full bg-[#272323] rounded-[5px] mt-2`}
          ></div>
        </div>
      );
    }

    return (
      <div
        onClick={() => handleSinglePost(post)}
        className={`mt-[15px] cursor-pointer w-full px-4 rounded-[5px] ${
          isExplore ? "h-[300px]" : "h-full"
        }`}
      >
        <div
          className={`w-full h-full grid gap-1 rounded-[5px] overflow-hidden`}
          style={{
            gridTemplateColumns: allMedia.length === 1 ? "1fr" : "1fr 1fr",
            gridTemplateRows:
              allMedia.length === 1
                ? "1fr"
                : allMedia.length === 2
                ? "1fr"
                : allMedia.length === 3
                ? "1fr 1fr"
                : "1fr 1fr",
          }}
        >
          {allMedia.slice(0, 4).map((media, index) => {
            // Show overlay with remaining count for 4+ media items
            if (index === 3 && allMedia.length > 4) {
              return (
                <div key={index} className="relative">
                  {media.type === "video" ? (
                    <VideoItem
                      url={media.url}
                      showOverlay={true}
                      remainingCount={allMedia.length - 4}
                    />
                  ) : (
                    <div className="relative w-full h-full">
                      <img
                        className="w-full h-full object-cover"
                        src={media.url}
                        alt="media"
                      />
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <span className={`text-white font-bold text-xl`}>
                          +{allMedia.length - 4}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              );
            }

            // Special layout for 3 media items
            if (index === 0 && allMedia.length === 3) {
              return (
                <div
                  key={index}
                  className="row-span-2"
                  style={{ gridRow: "span 2" }}
                >
                  {media.type === "video" ? (
                    <VideoItem url={media.url} />
                  ) : (
                    <img
                      className="w-full h-full object-cover"
                      src={media.url}
                      alt="media"
                    />
                  )}
                </div>
              );
            }

            // Standard media rendering
            return (
              <div key={index}>
                {media.type === "video" ? (
                  <VideoItem url={media.url} />
                ) : (
                  <img
                    className="w-full h-full object-cover"
                    src={media.url}
                    alt="media"
                  />
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  /**
   * Renders a single post/event item
   * @param {Object} post - The post to render
   * @returns {JSX.Element} - Post component
   */
  const renderPost = (post) => {
    const allMedia = getCombinedMedia(post);
    const isExplore = router?.pathname === "/dashboard/explore";
    return (
      <div
        className="w-full"
        key={post.id}
        onMouseEnter={() => {
          const timeoutId = setTimeout(() => handlePostHover(post), 1500);
          setHoverTimeout(timeoutId);
        }}
        onMouseLeave={() => {
          if (hoverTimeout) {
            clearTimeout(hoverTimeout);
            setHoverTimeout(null);
          }
        }}
      >
        <div
          className={`w-full h-full py-[15px] ${
            isExplore ? "border-b-[1px]" : "border-b-[5px]"
          } ${darktheme ? "border-[#323232]" : "border-[#EAECF0]"}`}
        >
          {/* Post header with user info */}
          <div className="px-4">
            <div className="flex gap-2 items-center">
              <Avatar
                name={post?.username}
                src={post?.userImage}
                width={"40px"}
                height={"40px"}
              />
              <div className="w-full">
                <div className="flex items-start justify-between w-full text-[13px]">
                  <div className="flex flex-col items-start">
                    <p
                      className={`${
                        darktheme ? "text-[#F9FAFB]" : "text-[#1C2024]"
                      } ml-1 -mb-1 text-sm font-semibold capitalize`}
                    >
                      {post?.username}
                    </p>
                    <div className="flex items-center gap-1">
                      <div className="flex items-center gap-1">
                        <img src={userLock} alt="" />
                        <p
                          className={`${
                            darktheme ? "text-[#F9FAFB]" : "text-[#667085]"
                          } font-medium text-xs`}
                        >
                          Arad User
                        </p>
                      </div>
                      <div className="w-[5px] h-[5px] bg-[#868C98] rounded-full ml-1"></div>
                      <p
                        className={`${
                          darktheme ? "text-[#F9FAFB]" : "text-[#667085]"
                        } m-[5px] text-[12px] font-medium flex items-center`}
                      >
                        {dayjs(post?.createdAt).fromNow()}
                      </p>
                    </div>
                  </div>

                  {/* Distance indicator if coordinates available */}
                  {token && post?.coords ? (
                    <div className="text-[#FC5C65] bg-[#FC5C651A] rounded-[15px] px-3 py-1 text-[12px] font-semibold ml-[6px]">
                      {getDistanceFromLatLonInKm(
                        coords?.latitude,
                        coords?.longitude,
                        post?.coords?.latitude,
                        post?.coords?.longitude
                      )}
                      km
                    </div>
                  ) : null}
                </div>
              </div>
            </div>

            {/* Post title and text */}
            <div
              onClick={() => handleSinglePost(post)}
              className={`mt-[.5rem] cursor-pointer font-semibold ${
                darktheme ? "text-[#F9FAFB]" : "text-[#525866]"
              } text-sm line-clamp-2`}
            >
              {post?.title}
            </div>
            <div
              onClick={() => handleSinglePost(post)}
              className={`mt-[.5rem] cursor-pointer font-normal ${
                darktheme ? "text-[#F9FAFB]" : "text-[#525866]"
              } text-sm line-clamp-2`}
            >
              {post?.text}
            </div>

            {/* Category badge if available */}
            {post?.category !== null && typeof post?.category === "object" && (
              <div
                className={`inline-flex items-center gap-1 px-4 py-2 rounded-full mt-2`}
                style={{
                  background: post?.category?.labelBgColor,
                }}
              >
                <RiAlarmWarningLine
                  size={16}
                  style={{
                    background: post?.category?.labelBgColor,
                    color: post?.category?.iconColor,
                  }}
                />
                <p
                  className={`text-xs font-bold`}
                  style={{ color: post?.category?.iconColor }}
                >
                  {post?.category?.title}
                </p>
              </div>
            )}
          </div>

          {/* Media content */}
          {renderMediaGrid(allMedia, post)}

          {/* Post interaction controls */}
          <UserPostShare
            theme={darktheme}
            key={post.id}
            updateCounts={updateCounts}
            isOpen={isOpen}
            onClose={onClose}
            handle={() => handleSinglePost(post)}
            user={user}
            token={token}
            openReportModal={openReportModal}
            selectedPost={post}
          />
        </div>
      </div>
    );
  };

  return (
    <InfiniteScroll
      dataLength={40}
      next={() => callEvents(true)}
      hasMore={true}
      scrollableTarget="scrollableDiv"
      scrollThreshold={1}
      key={1}
      loader={
        <div className="w-full px-3 justify-center">
          <SkeletonLoader />
          <SkeletonLoader />
        </div>
      }
      useWindow={true}
      className="w-[100vw] md:w-full"
      endMessage={<p>No more data to load.</p>}
    >
      <div
        className={`${
          darktheme ? "bg-kdark3 text-[#5c5c5e]" : "bg-[#fff] text-[#1D2939]"
        } rounded-[5px]`}
      >
        {events && events.length > 0 ? (
          events.map(renderPost)
        ) : (
          <div className="w-full px-3 justify-center">
            <SkeletonLoader />
            <SkeletonLoader />
          </div>
        )}
      </div>

      {/* Reference for intersection observer */}
      <div ref={loaderRef} />
    </InfiniteScroll>
  );
}

export default DashboardSide;
